import type Store from '@ember-data/store';
import { concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import formatDate from 'ember-intl/helpers/format-date';
import isEmpty from 'ember-truth-helpers/helpers/is-empty';
import FileAttachmentDisplayer from 'parog-web/components/file-attachment-displayer';
import type BioExamResultsReceivedJournalEvent from 'parog-web/models/bio-exam-results-received-journal-event';
import type Patient from 'parog-web/models/patient';
import type SemeiaSessionService from 'parog-web/services/session';
import { sortBy } from 'parog-web/utils/sort';
import PageContainer from 'ui-kit/components/page-container';

interface Signature {
  Args: {
    patient: Patient;
    je: BioExamResultsReceivedJournalEvent;
  };
}

export default class HlaConsultFormComponent extends Component<Signature> {
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  get hlaRelatedJes() {
    return this.#bioExamJes.filter(je => Boolean(je.hlaId));
  }

  get hlaRoute(): string {
    return this.session.patient
      ? 'patient-web.protected.hla.consult'
      : 'medics-web.protected.patients.patient.hla.consult';
  }

  get sortedHlaRelatedJes() {
    return this.hlaRelatedJes.sort(sortBy('occurredAt', 'desc'));
  }

  get #bioExamJes() {
    return this.args.patient.bioExamResultsReceivedJournalEvents;
  }

  <template>
    <PageContainer @dontUseTextWidthLimit={{true}} @title='Résultats HLA'>
      <:content>
        {{#if (isEmpty this.hlaRelatedJes)}}
          <span data-test-no-hla-results>Aucun résultat HLA connu.</span>
        {{else}}
          {{#each @je.orderedFileAttachments as |fileAttachment|}}
            <FileAttachmentDisplayer
              @shouldDisplayTextWithDownloadIcon={{true}}
              @downloadText={{concat
                'Résultats datant du '
                (formatDate @je.occurredAt)
              }}
              @fileAttachment={{fileAttachment}}
              @alternativeText='pièce jointe du résultat HLA'
            />
          {{/each}}

          Résultats plus anciens

          <table class='ui table'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Fichier</th>
              </tr>
            </thead>

            <tbody>
              {{#each this.sortedHlaRelatedJes as |je|}}
                <tr data-test-hla-tab-line={{je.id}}>
                  <td> {{formatDate je.occurredAt}} </td>
                  <td>
                    <LinkTo @route={{this.hlaRoute}} @model={{je.id}}>
                      Résultats HLA
                    </LinkTo>
                  </td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        {{/if}}
      </:content>
    </PageContainer>
  </template>
}
